// cleanup_convert_to_typescript_file_LOGIN_WORKFLOW
// cleanup_remove_external_libraries_LOGIN_WORKFLOW
// cleanup_convert_to_glu_component_LOGIN_WORKFLOW

import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { template } from 'lodash';

import { Button, PRIMARY, TERTIARY } from '@glu/buttons-react';

import Input from 'components/gluValidation/Input';
import Checkbox from 'components/checkbox/Checkbox';
import { challengeTypes } from 'constants.js';
import getMaskedValue from 'utils/challengeManagerMask';

const { EMAIL, SMS, VOICE } = challengeTypes;

const hasMultipleOtpOptions = ({
  email,
  isEmailOTPDisallowed,
  phoneNumber,
  voice,
  voiceEnabled
}) => {
  let optionCount = 0;
  if (phoneNumber) optionCount += 1;
  if (email && !isEmailOTPDisallowed) optionCount += 1;
  if (voiceEnabled && voice) optionCount += 1;
  return optionCount > 1;
};

const OTP = ({
  altMethodMessage,
  challengeErrorMessage,
  classes,
  data,
  deviceChallenge,
  isSubmitting,
  locale,
  onCancel,
  onChange,
  onChecked,
  onKeyDown,
  onSubmitForm,
  otpCode,
  primaryMethod,
  primaryMethodType,
  rememberMeFlag,
  resendCode,
  sendCodeToAltMethod,
  setShowAltMethod,
  showAltMethod,
  validate
}) => {
  const inputRef = useRef();

  useEffect(() => {
    if (inputRef?.current) {
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 500);
    }
  }, [inputRef?.current]);
  return (
    <>
      <div>
        <div className="modal-header">{locale['challengeManager.title']}</div>
        <div className="text">
          <div>
            {altMethodMessage ||
              template(locale['challengeManager.primaryMethodMessage'])({
                primaryMethod,
                primaryMethodType
              })}
          </div>
        </div>
      </div>
      <div className={classes.inputSection}>
        <div className={classes.inputField}>
          <Input
            forwardedRef={inputRef}
            disabled={isSubmitting}
            autoCorrect="off"
            className="form-group"
            data-qa="user-verification-input"
            id="user-verification-input"
            label={locale['challengeManager.enterCode']}
            name="user-verification-input"
            type="text"
            onChange={onChange}
            validators={[{ required: true, msg: locale['validation.required'] }]}
            onKeyDown={onKeyDown}
          />
          <span className={classes.error} data-testid="error-content">
            {challengeErrorMessage &&
              template(locale['challengeManager.invalidCode'])({
                numberOfFailedAttempts: data.numberOfFailedAttempts,
                numberOfAttemptsAllowed:
                  data.numberOfChallengesAllowedBeforeLockOut - data.numberOfFailedAttempts
              })}
          </span>
        </div>
        <div className={classes.resendLink}>
          <Button
            className="btn btn-tertiary"
            variant={TERTIARY}
            onClick={() => {
              resendCode();
            }}
          >
            {locale['challengeManager.resendCode']}
          </Button>
        </div>
      </div>
      {deviceChallenge && (
        <div className={classes.rememberMe}>
          <label htmlFor="rememberMe">
            <Checkbox
              onChange={(e) => onChecked(e.target.checked)}
              checked={rememberMeFlag}
              name="rememberMe"
              id="rememberMe"
              data-qa="rememberMe"
            />
            <span className="bold">{locale['challengeManager.rememberDevice']}</span>
          </label>
        </div>
      )}
      <div
        className={classnames(
          classes.altWay,
          hasMultipleOtpOptions(data) ? classes.show : classes.hide
        )}
      >
        {!showAltMethod ? (
          <Button
            className="btn btn-tertiary"
            variant={TERTIARY}
            onClick={() => {
              setShowAltMethod(true);
            }}
          >
            {locale['challengeManager.altWay']}
          </Button>
        ) : (
          <>
            {data.defaultContactType !== SMS && data.phoneNumber && (
              <Button
                classes="btn btn-tertiary"
                variant={TERTIARY}
                onClick={() => {
                  sendCodeToAltMethod(false, SMS);
                }}
                data-testid="altButton"
              >
                <div>
                  {template(locale['challengeManager.altMethodMessage'])({
                    altMethod: getMaskedValue({ challengeType: SMS, data }),
                    altMethodType: SMS
                  })}
                </div>
              </Button>
            )}
            <br />
            {data.defaultContactType !== EMAIL && !data.isEmailOTPDisallowed && data.email && (
              <Button
                classes="btn btn-tertiary"
                variant={TERTIARY}
                onClick={() => {
                  sendCodeToAltMethod(false, EMAIL);
                }}
                data-testid="altButton"
              >
                <div>
                  {template(locale['challengeManager.altMethodMessage'])({
                    altMethod: getMaskedValue({ challengeType: EMAIL, data }),
                    altMethodType: EMAIL
                  })}
                </div>
              </Button>
            )}
            <br />
            {data.defaultContactType !== VOICE && data.voiceEnabled && data.voice?.value && (
              <Button
                classes="btn btn-tertiary"
                variant={TERTIARY}
                onClick={() => {
                  sendCodeToAltMethod(false, VOICE);
                }}
                data-testid="altButton"
              >
                <div>
                  {template(locale['challengeManager.altMethodMessage'])({
                    altMethod: getMaskedValue({ challengeType: VOICE, data }),
                    altMethodType: VOICE
                  })}
                </div>
              </Button>
            )}
          </>
        )}
      </div>
      <section>
        <Button
          className="btn btn-primary"
          data-qa="modal-notification-primary-btn"
          text={isSubmitting ? locale['common.submitting'] : locale['common.continue']}
          disabled={!otpCode || isSubmitting}
          variant={PRIMARY}
          onClick={async (e) => {
            const { isValid } = await validate();
            if (isValid) onSubmitForm(e);
          }}
          data-testid="continueButton"
        />
        <Button
          className="btn btn-tertiary"
          onClick={onCancel}
          data-qa="modal-notification-secondary-btn"
          text={locale['common.cancel']}
          variant={TERTIARY}
          data-testid="cancelButton"
        />
      </section>
    </>
  );
};

OTP.propTypes = {
  challengeErrorMessage: PropTypes.bool,
  classes: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({}),
  deviceChallenge: PropTypes.bool,
  isSubmitting: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  locale: PropTypes.shape({}).isRequired,
  onCancel: PropTypes.func.isRequired,
  onChecked: PropTypes.func.isRequired,
  onSubmitForm: PropTypes.func.isRequired,
  otpCode: PropTypes.string.isRequired,
  primaryMethod: PropTypes.string.isRequired,
  primaryMethodType: PropTypes.string.isRequired,
  rememberMeFlag: PropTypes.bool,
  resendCode: PropTypes.func.isRequired,
  sendCodeToAltMethod: PropTypes.func.isRequired,
  showAltMethod: PropTypes.bool.isRequired,
  setShowAltMethod: PropTypes.func.isRequired,
  validate: PropTypes.func.isRequired,
  altMethodMessage: PropTypes.string
};

OTP.defaultProps = {
  challengeErrorMessage: false,
  data: {},
  deviceChallenge: false,
  rememberMeFlag: false,
  altMethodMessage: null
};

export default OTP;
